import React, { useCallback, useEffect, useState } from 'react';
import { devTaskService } from '../services/api';

const AdminDevTasks = ({ user }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', logic: '', inputs: '', outputs: '', status: 'todo' });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { items, total } = await devTaskService.list({ page, page_size: pageSize, q, status });
      setItems(items);
      setTotal(total);
    } catch (e) {
      console.error('Load dev tasks error', e);
      alert('Chyba pri načítaní úloh');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, q, status]);

  useEffect(() => { load(); }, [load]);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await devTaskService.update(editing.id, form);
      } else {
        await devTaskService.create(form);
      }
      setForm({ title: '', logic: '', inputs: '', outputs: '', status: 'todo' });
      setEditing(null);
      load();
    } catch (e) {
      console.error('Save dev task error', e);
      const details = e.response?.data?.details;
      alert(details?.map(d=>d.msg).join(', ') || e.response?.data?.error || 'Chyba pri uložení');
    }
  };

  if (!user || user.role !== 'admin') {
    return <div style={{ padding: 20 }}>Prístup zamietnutý</div>;
  }

  return (
    <div style={{ padding: 20 }}>
      <h1>🛠️ Development Tasks</h1>

      <div style={{ display:'flex', gap:10, marginBottom:12 }}>
        <input placeholder="Hľadať..." value={q} onChange={e=>{ setQ(e.target.value); setPage(1); }} />
        <select value={status} onChange={e=>{ setStatus(e.target.value); setPage(1); }}>
          <option value="">(všetky)</option>
          <option value="todo">todo</option>
          <option value="in_progress">in_progress</option>
          <option value="tested">tested</option>
          <option value="done">done</option>
        </select>
        <select value={pageSize} onChange={e=>{ setPageSize(parseInt(e.target.value)||20); setPage(1); }}>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
        <button onClick={load} disabled={loading}>Obnoviť</button>
      </div>

      <form onSubmit={onSubmit} style={{ border:'1px solid #ddd', padding:12, borderRadius:6, marginBottom:16 }}>
        <h3>{editing ? 'Upraviť úlohu' : 'Pridať úlohu'}</h3>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
          <div>
            <label>Názov</label>
            <input required value={form.title} onChange={e=>setForm({ ...form, title:e.target.value })} />
          </div>
          <div>
            <label>Status</label>
            <select value={form.status} onChange={e=>setForm({ ...form, status:e.target.value })}>
              <option value="todo">todo</option>
              <option value="in_progress">in_progress</option>
              <option value="tested">tested</option>
              <option value="done">done</option>
            </select>
          </div>
          <div style={{ gridColumn:'1 / span 2' }}>
            <label>Logika</label>
            <textarea rows={3} value={form.logic} onChange={e=>setForm({ ...form, logic:e.target.value })} />
          </div>
          <div>
            <label>Inputs</label>
            <textarea rows={3} value={form.inputs} onChange={e=>setForm({ ...form, inputs:e.target.value })} />
          </div>
          <div>
            <label>Outputs</label>
            <textarea rows={3} value={form.outputs} onChange={e=>setForm({ ...form, outputs:e.target.value })} />
          </div>
        </div>
        <div style={{ marginTop:10, display:'flex', gap:8 }}>
          <button type="submit">{editing ? 'Uložiť' : 'Pridať'}</button>
          {editing && <button type="button" onClick={()=>{ setEditing(null); setForm({ title:'', logic:'', inputs:'', outputs:'', status:'todo' }); }}>Zrušiť edit</button>}
        </div>
      </form>

      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead>
          <tr style={{ background:'#f5f5f5' }}>
            <th style={{ textAlign:'left', padding:8, border:'1px solid #eee' }}>Názov</th>
            <th style={{ textAlign:'left', padding:8, border:'1px solid #eee' }}>Status</th>
            <th style={{ textAlign:'left', padding:8, border:'1px solid #eee' }}>Aktualizované</th>
            <th style={{ padding:8, border:'1px solid #eee' }}></th>
          </tr>
        </thead>
        <tbody>
          {items.map(t => (
            <tr key={t.id}>
              <td style={{ padding:8, border:'1px solid #eee' }}>
                <div style={{ fontWeight:'bold' }}>{t.title}</div>
                {t.logic && <div style={{ color:'#666', fontSize:12 }}>Logika: {t.logic}</div>}
                {t.inputs && <div style={{ color:'#666', fontSize:12 }}>Inputs: {t.inputs}</div>}
                {t.outputs && <div style={{ color:'#666', fontSize:12 }}>Outputs: {t.outputs}</div>}
              </td>
              <td style={{ padding:8, border:'1px solid #eee' }}>{t.status}</td>
              <td style={{ padding:8, border:'1px solid #eee' }}>{t.updated_at || t.created_at}</td>
              <td style={{ padding:8, border:'1px solid #eee', textAlign:'right' }}>
                <button onClick={()=>{ setEditing(t); setForm({ title:t.title||'', logic:t.logic||'', inputs:t.inputs||'', outputs:t.outputs||'', status:t.status||'todo' }); }}>Upraviť</button>
                <button style={{ marginLeft:8 }} onClick={async ()=>{ if (window.confirm('Zmazať úlohu?')) { await devTaskService.remove(t.id); load(); } }}>Zmazať</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display:'flex', justifyContent:'space-between', marginTop:10 }}>
        <div>Spolu: {total}</div>
        <div>
          <button disabled={page<=1} onClick={()=>setPage(p=>p-1)}>← Predošlá</button>
          <span style={{ margin:'0 8px' }}>Strana {page}</span>
          <button disabled={(page*pageSize)>=total} onClick={()=>setPage(p=>p+1)}>Ďalšia →</button>
        </div>
      </div>
    </div>
  );
};

export default AdminDevTasks;
